import type { ModelWire } from './bake-wire.ts';
import { adoptBaked, isBaked } from './library.ts';

/**
 * Bakes ahead in a worker (bake.worker.ts): the page asks for every model a fight will need, and takes each one
 * in, in order, as it comes back (rebuilding it only for its materials). Without workers (Node, tests,
 * `?bakeWorker=0`), or when one fails or takes too long, a model is baked on the page as before.
 */
type Reply = { key: string; wire?: ModelWire; error?: string };

/** The worker silent this long (nothing back since a key was asked), and the page bakes it itself. */
const WAIT_MAX_MS = 8000;

let worker: Worker | null | undefined;
const asked = new Map<string, number>();
const replies = new Map<string, Reply>();
/** When the worker last sent something back. */
let heard = 0;

const start = (): Worker | null => {
  if (worker !== undefined) return worker;
  worker = null;
  if (typeof Worker === 'undefined' || typeof location === 'undefined' || /[?&]bakeWorker=0/.test(location.search)) return null;
  try {
    const w = new Worker(new URL('./bake.worker.ts', import.meta.url), { type: 'module' });
    w.onmessage = (e: MessageEvent<Reply>): void => {
      heard = performance.now();
      if (asked.has(e.data.key)) replies.set(e.data.key, e.data);
    };
    // A worker that can't start (or dies) leaves every model to the page.
    w.onerror = (): void => {
      worker = null;
      w.terminate();
    };
    worker = w;
  } catch {
    worker = null;
  }
  return worker;
};

export const bakePool = {
  /** Starts the worker early, so its own load is done before the first fight asks. */
  warm(): void {
    start();
  },

  /** Sends `keys` (in the order they'll be taken) to the worker. */
  ask(keys: readonly string[]): void {
    const w = start();
    if (!w) return;
    const now = performance.now();
    for (const k of keys) {
      if (isBaked(k) || asked.has(k)) continue;
      asked.set(k, now);
      w.postMessage({ key: k });
    }
  },

  /**
   * True while `key` is still in the worker. Once it is back, takes it in (the page's library has it) and says
   * false; also false for a key never asked, or one the worker failed or kept too long (the page bakes it).
   */
  waiting(key: string): boolean {
    const t = asked.get(key);
    if (t === undefined) return false;
    const r = replies.get(key);
    if (!r) {
      if (worker && performance.now() - Math.max(t, heard) < WAIT_MAX_MS) return true;
      asked.delete(key);
      return false;
    }
    asked.delete(key);
    replies.delete(key);
    if (r.wire) {
      try {
        adoptBaked(key, r.wire);
      } catch {
        // Baked on the page instead.
      }
    }
    return false;
  },
};
